import { ref, get, update, push, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import { loadListTasks } from './transactionTasks';
import { planNotifications, dueNotifications, resolveRecipients, dispatchNotification } from './notificationPlan';
import { getUserContact, getUsersByRole } from './userService';
import { postEmail } from './emailSend';
import { groupInternal } from './groupRecipients';

// Auto emails that go out when a seller transaction is created.
// Only automatic Notification rows on the seller listing list are used, and they never appear on task lists.
const SELLER_CREATED_LIST = 'seller-listing';
const ROLE_LOOKUPS = ['TC', 'Marketing Manager', 'Local Assistant'];

// Formats a YYYY-MM-DD date as "Tuesday, October 20, 2026".
const longDate = (value) => {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  return isNaN(d) ? String(value) : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

// Partners of a type (for example, Photographer) with their email, keyed to the name used on the form.
const partnersOfType = async (partnerType) => {
  const snap = await get(ref(database, 'partners'));
  if (!snap.exists()) return [];
  return Object.values(snap.val())
    .filter((p) => p && p.partnerType === partnerType)
    .map((p) => ({
      formName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.companyName || '',
      displayName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.companyName || '',
      email: p.email || '',
    }));
};

// Fills {{merge fields}} in a subject or body. Unknown fields become blank.
const fillMerge = (text, data) =>
  String(text || '').replace(/\{\{(\w+)\}\}/g, (_, key) => (data[key] == null ? '' : String(data[key])));

// One line per co-agent: name, email and phone from their profile. Empty when there is no co-agent.
const coAgentLineFor = (coAgents) => coAgents
  .filter(a => a && (a.displayName || a.email))
  .map(a => `<br><strong>Co-agent:</strong> ${[a.displayName, a.email, a.phone].filter(Boolean).join(', ')}`)
  .join('');

// Co-agents on the form, with their profile contact details (the form stores only name and email).
const coAgentsFor = async (tx) => {
  const listed = [...(tx.coListAgents || []), ...(tx.coSellingAgents || [])].filter(a => a && (a.uid || a.email));
  return Promise.all(listed.map(async (a) => {
    const profile = a.uid ? await getUserContact(a.uid) : null;
    return profile || { firstName: '', lastName: '', displayName: a.name || '', email: a.email || '', phone: '' };
  }));
};

// Every seller on the deal: the primary seller plus any additional contacts marked as sellers.
const sellerContactsBlock = (tx) => {
  const people = [
    { name: [tx.firstName, tx.lastName].filter(Boolean).join(' '), phone: tx.phone, email: tx.email },
    ...((tx.additionalContacts || []).filter((c) => c && (c.firstName || c.lastName || c.email || c.phone))
      .map((c) => ({ name: [c.firstName, c.lastName].filter(Boolean).join(' '), phone: c.phone, email: c.email }))),
  ].filter((p) => p.name || p.email || p.phone);
  return people.map((p) => `<p>${[p.name, p.phone, p.email].filter(Boolean).join(', ')}</p>`).join('');
};

// The listing agent and any co-agent, with name, email and phone.
const agentsBlock = (agent, coAgents) => {
  const lines = [agent, ...coAgents].filter((a) => a && (a.displayName || a.email));
  return lines.map((a) => `<p>${[a.displayName, a.email, a.phone].filter(Boolean).join(', ')}</p>`).join('');
};

// Merge data taken from the deal and the agent's profile.
const mergeDataFor = (tx, agent, coAgents) => ({
  co_agent_line: coAgentLineFor(coAgents),
  agent_full_name: [agent?.firstName, agent?.lastName].filter(Boolean).join(' '),
  agent_email: agent?.email || '',
  photographer_name: tx.photographer || '',
  stager_name: tx.stager || '',
  photography_date: longDate(tx.photographyDate),
  anticipated_listing_date: longDate(tx.anticipatedListingDate),
  listing_effective_date: longDate(tx.listingEffectiveDate),
  seller_contacts_block: sellerContactsBlock(tx),
  agents_block: agentsBlock(agent, coAgents),
  listing_price: tx.listingPrice ? (String(tx.listingPrice).startsWith('$') ? String(tx.listingPrice) : `$${tx.listingPrice}`) : '',
  notes_line: tx.photographerNotes ? `<p><strong>Notes:</strong> ${tx.photographerNotes}</p>` : '',
  address: tx.propertyAddress || '',
  seller_names: [tx.firstName, tx.lastName].filter(Boolean).join(' '),
  seller_phone: tx.phone || '',
  seller_email: tx.email || '',
  agent_first_name: agent?.firstName || '',
  agent_last_name: agent?.lastName || '',
  agent_phone: agent?.phone || '',
});

// The one place an email is handed to a sending service. Until an email service is connected,
// it only logs, and dispatchNotification will not call it unless the master switch is on.
const sendEmailMessage = async (to, subject, html, replyTo = [], fromName = '', cc = [], meta = {}) => {
  const r = await postEmail({ to, cc, replyTo, subject, html, fromName, automated: true, recipientRole: meta.role || '', name: meta.name || '', transactionId: meta.transactionId || null });
  return { success: !!r.sent, queued: !!r.queued, approvalId: r.approvalId || null, testMode: !!r.testMode, reason: r.reason || null, to, cc, replyTo, fromName, sentAt: new Date().toISOString(), htmlLength: html.length };
};

// Agent-sent emails copy the listing agent and any co-agent on the email itself.
// Tina-sent emails copy no one; replies go to her alone.
const ccFor = (sender, recipientEmail, agent, coAgents) => {
  if (sender === 'TC') return [];
  const list = [agent?.email, ...coAgents.map(c => c.email)].filter(Boolean);
  return [...new Set(list)].filter(e => e.toLowerCase() !== String(recipientEmail).toLowerCase());
};

// Replies go to the listing agent, any co-agent, and the transaction coordinator (Tina).
// Agent-sent emails: replies go to the listing agent, any co-agent, and Tina.
// Tina-sent emails: replies go to Tina only, since the agents aren't on those emails.
const replyToFor = (sender, agent, coAgents, tcUsers) => {
  const tcEmails = tcUsers.map(u => u.email);
  const list = sender === 'TC'
    ? tcEmails
    : [agent?.email, ...coAgents.map(c => c.email), ...tcEmails];
  return [...new Set(list.filter(Boolean))];
};

// Keeps a record of every email on the transaction, so the agent and team can review it.
const logCommunication = async (transactionId, record) => {
  const commRef = push(ref(database, `transactions/${transactionId}/communications`));
  await set(commRef, record);
};

// Sends the seller created emails for a new transaction, once per email.
export const sendSellerCreatedEmails = async (transactionId, tx, ownerUid, onlyNames = null) => {
  const tasks = await loadListTasks(SELLER_CREATED_LIST);
  const plan = planNotifications(tasks, {}, tx.createdAt, tx);
  // Per-deal switch (defaults on) and start date: nothing dated before the deal's automation start sends.
  if (tx.automatedEmailsEnabled === false) return [];
  const startDay = String(tx.automationStartDate || tx.importedAt || tx.createdAt || '').slice(0, 10);
  const due = dueNotifications(plan).filter((item) => (!onlyNames || onlyNames.includes(item.name)) && (!startDay || !item.sendOn || item.sendOn >= startDay));
  if (due.length === 0) return [];

  const agent = await getUserContact(ownerUid);
  const coAgents = await coAgentsFor(tx);
  const usersByRole = {};
  for (const role of ROLE_LOOKUPS) usersByRole[role] = await getUsersByRole(role);
  // The photographer chosen on the seller form (matched by the name saved on the form).
  const photographers = await partnersOfType('Photographer');
  usersByRole.Photographer = photographers.filter((p) => p.formName === tx.photographer);
  // The stager chosen on the seller form (matched by the name saved on the form).
  const stagers = await partnersOfType('Stager');
  usersByRole.Stager = stagers.filter((p) => p.formName === tx.stager);
  const merge = mergeDataFor(tx, agent, coAgents);
  const sentRef = ref(database, `transactions/${transactionId}/autoEmailsSent`);
  const sentSnap = await get(sentRef);
  const alreadySent = sentSnap.exists() ? sentSnap.val() : {};

  const outcomes = [];
  for (const item of due) {
    const key = item.name.replace(/[.#$\[\]\/]/g, '-');
    if (alreadySent[key]) continue;
    const resolvedAll = resolveRecipients(item, { deal: tx, agent, usersByRole });
    // Internal team: ONE email to the listing agent with the rest of the team CC'd. Outside recipients unchanged.
    const agentCc = item.sender === 'TC' ? [] : [agent?.email, ...coAgents.map(c => c.email)].filter(Boolean);
    const { group, others } = groupInternal(resolvedAll.recipients, { primaryEmail: agent?.email, extraCc: agentCc });
    const resolved = { ...resolvedAll, recipients: [...(group ? [{ ...group.primary, groupCc: group.cc, groupSize: group.members.length }] : []), ...others] };
    const result = await dispatchNotification(item, resolved, async (planned, recipient) => {
      const grouped = Array.isArray(recipient.groupCc) && recipient.groupSize > 1;
      const perPerson = { ...merge, recipient_first_name: grouped ? 'Team' : ((recipient.name || '').split(' ')[0] || '') };
      const subject = fillMerge(planned.subject, perPerson);
      const body = fillMerge(planned.body, perPerson);
      const fromName = planned.sender === 'TC'
        ? (usersByRole.TC?.[0]?.displayName || 'Transaction Coordinator')
        : [agent?.firstName, agent?.lastName].filter(Boolean).join(' ');
      const cc = Array.isArray(recipient.groupCc) ? recipient.groupCc : ccFor(planned.sender, recipient.email, agent, coAgents);
      const sent = await sendEmailMessage(recipient.email, subject, body, replyToFor(planned.sender, agent, coAgents, usersByRole.TC || []), fromName, cc, { role: recipient.role, name: planned.name, transactionId });
      await logCommunication(transactionId, {
        kind: 'auto',
        name: planned.name,
        toName: recipient.name || '',
        ...(Array.isArray(recipient.groupCc) ? { grouped: true } : {}),
        toEmail: recipient.email,
        role: recipient.role,
        subject,
        body,
        sentAt: sent.sentAt,
        sender: planned.sender,
        fromName: sent.fromName,
        cc: sent.cc,
        replyTo: sent.replyTo,
        status: sent.queued ? 'awaiting_approval' : sent.success ? (sent.testMode ? 'test_sent' : 'sent') : `not_sent: ${sent.reason || 'error'}`,
        ...(sent.approvalId ? { approvalId: sent.approvalId } : {}),
      });
      return sent;
    });
    outcomes.push({ name: item.name, ...result });
    if (result.sent && (result.results || []).some((x) => x.result && (x.result.success || x.result.queued))) await update(sentRef, { [key]: new Date().toISOString() });
  }
  return outcomes;
};
